/**
 * Tool system - defines tool types, materials, and properties
 *
 * Tools are stored in inventory slots as: { toolId, toolType, material, durability, maxDurability }
 * (instead of blockId/count for blocks)
 *
 * Tool types: 'pickaxe', 'shovel', 'axe', 'sword', 'hoe'
 * Materials: 'wood', 'stone', 'iron', 'gold', 'diamond'
 */

// Tool material properties: [miningSpeed, durability, attackDamage]
export const TOOL_MATERIALS = {
  wood:    { speed: 2,  durability: 59,   damage: 0 },
  stone:   { speed: 4,  durability: 131,  damage: 1 },
  iron:    { speed: 6,  durability: 250,  damage: 2 },
  gold:    { speed: 12, durability: 32,   damage: 0 },
  diamond: { speed: 8,  durability: 1561, damage: 3 },
};

// Tool type attack damage bonus
export const TOOL_ATTACK_DAMAGE = {
  sword: 4,    // base 4 + material bonus
  pickaxe: 2,
  axe: 3,
  shovel: 1,
  hoe: 1,
};

// Unique tool IDs (negative numbers to distinguish from block IDs)
let _nextToolId = 1;
export function createTool(toolType, material) {
  const mat = TOOL_MATERIALS[material];
  return {
    toolId: _nextToolId++,
    toolType,
    material,
    durability: mat.durability,
    maxDurability: mat.durability,
  };
}

export function isTool(item) {
  return item && item.toolId !== undefined;
}

export function getToolSpeed(item) {
  if (!isTool(item)) return 1;
  return TOOL_MATERIALS[item.material].speed;
}

export function getToolDamage(item) {
  if (!isTool(item)) return 1; // fist damage
  return TOOL_ATTACK_DAMAGE[item.toolType] + TOOL_MATERIALS[item.material].damage;
}

// Check if tool is effective on this block type
// requiredTool comes from BlockType.getToolType(blockId)
export function isToolEffective(item, requiredTool) {
  if (!isTool(item)) return false;
  return item.toolType === requiredTool;
}

// Nivel de cosecha de la herramienta según su material
// wood = 1, stone = 2, iron = 3, gold = 1, diamond = 4
export function getToolHarvestLevel(item) {
  if (!isTool(item)) return 0; // mano desnuda = nivel 0
  const levels = {
    wood: 1,
    stone: 2,
    iron: 3,
    gold: 1,
    diamond: 4,
  };
  return levels[item.material] || 0;
}

export function damageTool(item, amount = 1) {
  if (!isTool(item)) return item;
  item.durability -= amount;
  if (item.durability <= 0) return null; // tool breaks
  return item;
}

// Returns durability as a 0-1 fraction (0 = broken, 1 = full)
export function getToolDurabilityPercent(item) {
  if (!isTool(item) || !item.maxDurability) return 1;
  return Math.max(0, Math.min(1, item.durability / item.maxDurability));
}

// Returns a hex color string based on current durability percentage
export function getToolDurabilityColor(item) {
  const pct = getToolDurabilityPercent(item);
  if (pct > 0.75) return '#4CAF50';       // green
  if (pct > 0.50) return '#8BC34A';       // yellow-green
  if (pct > 0.25) return '#FFC107';       // yellow
  if (pct > 0.10) return '#FF9800';       // orange
  return '#F44336';                        // red
}

// Get display name for tool (Spanish)
export function getToolName(item) {
  if (!isTool(item)) return '';
  const materials = {
    wood: 'Madera', stone: 'Piedra', iron: 'Hierro', gold: 'Oro', diamond: 'Diamante'
  };
  const types = {
    pickaxe: 'Pico', shovel: 'Pala', axe: 'Hacha', sword: 'Espada', hoe: 'Azadón'
  };
  return `${types[item.toolType]} de ${materials[item.material]}`;
}

// Get color for tool material (for rendering in hotbar/inventory)
export function getToolColor(item) {
  if (!isTool(item)) return '#888';
  const colors = {
    wood: '#8B6B3E',
    stone: '#8A8A8A',
    iron: '#D8D8D8',
    gold: '#FFD700',
    diamond: '#4AEDD9',
  };
  return colors[item.material] || '#888';
}
